import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePlaidLink } from "react-plaid-link";
import { useCreatePlaidSessionToken } from "@lending/hooks/queries/Plaid/useCreatePlaidSessionToken";
import { useIsPlaidLayerEnabled } from "@lending/hooks/useIsPlaidLayerEnabled";
import { useFlowNavigation } from "@lending/navigation";
import { PREFILL_DATA_SOURCE } from "@lending/types";
import { useStore } from "../contexts/StoreContext";
import type { PlaidHandlerSubmissionData, PlaidLinkStableEvent } from "react-plaid-link";

interface PlaidHandler {
  open: () => void;
  ready: boolean;
  error: ErrorEvent | null;
  exit: () => void;
  submit: (data: PlaidHandlerSubmissionData) => void;
}

interface UsePlaidLayerReturn {
  plaidHandler: PlaidHandler | null;
}

/**
 * Hook that manages the full Plaid Layer lifecycle:
 * token fetching, SDK initialization, event handling, and token refresh.
 *
 * All event handlers are hardcoded since only PlaidPhoneNumber uses this hook:
 * - onSuccess: saves publicToken to store (will navigate to personal-info-review)
 * - LAYER_NOT_AVAILABLE: navigates to manual entry (../personal-info)
 * - LAYER_READY: auto-opens the Plaid modal
 * - onExit: fetches a fresh session token so the user can retry
 */
function usePlaidLayer(): UsePlaidLayerReturn {
  const queryClient = useQueryClient();
  const { goNext } = useFlowNavigation();
  const { updateData } = useStore();

  // Only fetch a session token when the institution has Plaid Layer configured.
  // Without this gate the hook fires a POST on every mount, which fails with a
  // server error for institutions that have no plaid_api_layer_template_id.
  const isPlaidLayerEnabled = useIsPlaidLayerEnabled();
  const { data: sessionData } = useCreatePlaidSessionToken({ enabled: isPlaidLayerEnabled });
  const plaidLinkToken = sessionData?.link_token ?? null;

  // Store navigate and updateData in refs so usePlaidLink handlers stay stable
  // (avoids handler re-creation when these change).
  const goNextRef = useRef(goNext);
  const updateDataRef = useRef(updateData);

  useEffect(() => {
    goNextRef.current = goNext;
  }, [goNext]);

  useEffect(() => {
    updateDataRef.current = updateData;
  }, [updateData]);

  const isPlaidModalOpenRef = useRef(false);

  // Called when Plaid Layer completes successfully with a public_token.
  const onSuccessHandler = useCallback(
    (publicToken: string) => {
      isPlaidModalOpenRef.current = false;
      updateDataRef.current({
        plaidPiiFormData: undefined,
        plaidPublicToken: publicToken,
        skippedPlaidPrefill: false,
        prefillDataSource: PREFILL_DATA_SOURCE.PLAID_LAYER,
        lendingApplicationID: undefined,
      });
      // Invalidate so a fresh token is fetched if the user navigates back.
      // Plaid link tokens are single-use — the consumed token cannot be reused.
      void queryClient.invalidateQueries({ queryKey: ["plaidSessionToken"] });
      // Navigate to the non-canonical (no UUID) personal-info-review path.
      // A fresh Plaid token means a new application will be created on submit,
      // so the URL should not carry the previous application's UUID.
      void goNextRef.current({ applicationUuid: undefined });
    },
    [queryClient]
  );

  // Ref to solve circular dependency: onEvent needs open(), but open depends on onEvent.
  const openRef = useRef<(() => void) | null>(null);

  // Handle Plaid lifecycle events.
  const onEventHandler = useCallback(
    (eventName: PlaidLinkStableEvent | string) => {
      if (eventName === "LAYER_READY") {
        openRef.current?.();
      }

      if (eventName === "OPEN") {
        isPlaidModalOpenRef.current = true;
      }

      // LAYER_NOT_AVAILABLE fires when the phone number is not found in Plaid's network.
      // Invalidate the token — the Plaid session is over and the token cannot be reused.
      if (eventName === "LAYER_NOT_AVAILABLE") {
        console.log("Plaid Layer not available, navigating to manual entry");
        isPlaidModalOpenRef.current = false;
        void queryClient.invalidateQueries({ queryKey: ["plaidSessionToken"] });
        updateDataRef.current({
          skippedPlaidPrefill: true,
          plaidPublicToken: undefined,
          plaidPiiFormData: undefined,
          prefillDataSource: PREFILL_DATA_SOURCE.MANUAL,
          // Drop the previous application's UUID. If a prior Plaid attempt
          // created an application, the manual flow must not re-use it.
          lendingApplicationID: undefined,
          // Clear PII captured from any prior attempt so the manual flow starts
          // from a clean slate. Phone and email are preserved (entered on this
          // screen) so the user doesn't have to re-enter them on the next step.
          tax_id: undefined,
          date_of_birth: undefined,
        });
        // Drop UUID context: navigate to the non-canonical (no UUID) manual path
        // so we don't strand the user on the previous application's canonical
        // route. Mirrors onSuccess at line 75.
        goNextRef.current({ skippedPlaidPrefill: true, applicationUuid: undefined });
      }
    },
    [queryClient]
  );

  // Handle when user manually closes the Plaid modal.
  // Fetch a fresh session token so the user can retry.
  const onExitHandler = useCallback(() => {
    isPlaidModalOpenRef.current = false;
    updateDataRef.current({ plaidPublicToken: undefined, plaidPiiFormData: undefined });
    void queryClient.invalidateQueries({ queryKey: ["plaidSessionToken"] });
  }, [queryClient]);

  // Initialize the Plaid Link handler. As soon as the token is available,
  // usePlaidLink begins preloading the Plaid SDK in the background.
  const { open, ready, error, exit, submit } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: onSuccessHandler,
    onExit: onExitHandler,
    onEvent: onEventHandler,
  });

  useEffect(() => {
    openRef.current = open as () => void;
  }, [open]);

  const plaidHandler = useMemo(
    () =>
      plaidLinkToken
        ? {
            open: open as () => void,
            ready,
            error,
            exit: exit as () => void,
            submit: submit as (data: PlaidHandlerSubmissionData) => void,
          }
        : null,
    [plaidLinkToken, open, ready, error, exit, submit]
  );

  return { plaidHandler };
}

export { usePlaidLayer };
export type { PlaidHandler, UsePlaidLayerReturn };
